import React, { useState } from 'react';

const NO_EXTRA = { source: null, items: [], cursor: null, fetched: false, loading: false, error: '' };

/**
 * "Show more" for a keyset-paginated section. `first` is the first page's
 * response (or null while it loads), `itemsKey` names its list, and
 * `fetchPage(cursor)` loads the page after `cursor`.
 *
 * Later pages are kept with the first page they follow, so a new first page
 * (another period or student) drops them, and a late answer for the old one
 * is ignored.
 */
export function usePagedItems(first, itemsKey, fetchPage) {
  const [extra, setExtra] = useState(NO_EXTRA);
  const mine = extra.source === first ? extra : { ...NO_EXTRA, source: first };
  const items = first ? [...first[itemsKey], ...mine.items] : [];
  const cursor = mine.fetched ? mine.cursor : first?.next_cursor;

  const loadMore = async () => {
    if (!cursor) return;
    setExtra({ ...mine, loading: true, error: '' });
    try {
      const page = await fetchPage(cursor);
      setExtra((prev) => (prev.source !== first ? prev : {
        ...prev,
        items: [...prev.items, ...page[itemsKey]],
        cursor: page.next_cursor,
        fetched: true,
        loading: false,
      }));
    } catch {
      setExtra((prev) => (prev.source !== first ? prev : {
        ...prev, loading: false, error: 'Could not load more. Please try again.',
      }));
    }
  };

  return { items, hasMore: Boolean(cursor), loadMore, loadingMore: mine.loading, moreError: mine.error };
}

/** The "Show more" button and its error, for a usePagedItems result. */
export const ShowMore = ({ paged, what }) => (paged.hasMore || paged.moreError ? (
  <div className="td-act-more">
    {paged.moreError ? <p className="td-error" role="alert">{paged.moreError}</p> : null}
    {paged.hasMore ? (
      <button type="button" className="td-btn td-btn--small" onClick={paged.loadMore} disabled={paged.loadingMore}>
        {paged.loadingMore ? 'Loading…' : `Show more ${what}`}
      </button>
    ) : null}
  </div>
) : null);
